import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import isStrongPassword from 'validator/lib/isStrongPassword';
import isMobilePhone from 'validator/lib/isMobilePhone';
import getRecaptchaToken from '../helpers/getRecaptchaToken';

import '../styles/AccountRegistrationPage.css';
import sanitizeName from '../helpers/validators/sanitizeName';
import AppContext from './context/AppContext';
import { bool } from 'prop-types';

const AccountRegistrationPage = ({ isMobile, isLoggedIn }) => {
    const { appContext } = useContext(AppContext);
    const navigate = useNavigate();
    const [isValidRequest, setIsValidRequest] = useState(true);
    const [isProcessing, setIsProcessing] = useState(true);
    const [isProcessed, setIsProcessed] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [acceptsMarketing, setAcceptsMarketing] = useState(false);
    const [startRegistration, setStartRegistration] = useState(false);
    const handleVerificationRequest = useCallback(async () => {
        try {
            if (!email
                || !isEmail(email)
                || !password
                || !isStrongPassword(password)) {
                setIsValidRequest(false);
                setIsProcessing(false);
                return;
            }
            setIsProcessing(true);
            setIsProcessed(false);
            setErrorMessage(null);
            const response = await fetch(new URL('/api/customer/register', process.env.APP_URL), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                    email,
                    password,
                    firstName,
                    lastName,
                    phone,
                    acceptsMarketing,
                }),
            });
            const data = await response.json();
            setIsProcessing(false);
            if (!data.success) {
                if (data.error) {
                    setErrorMessage(data.error);
                }
                setIsProcessed(false);
                return;
            }
            setIsProcessed(true);
        } catch (e) {
            setIsProcessing(false);
            setErrorMessage(e.message);
        }
    }, [email, password, firstName, lastName, phone, acceptsMarketing]);
    useEffect(() => {
        const navigateTimeoutFunc = () => {
            navigate('/');
        };
        const navigateTimer = null;
        const NAVIGATE_TIMEOUT_MS = 3000;
        if (isProcessed === true
            || isLoggedIn === true) {
            setTimeout(navigateTimeoutFunc, NAVIGATE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(navigateTimer);
        };
    }, [isProcessed, isLoggedIn]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isLoggedIn === false) {
            setStartRegistration(true);
            handleVerificationRequest();
        }
    };
    const updateName = (e, setName = () => { }) => {
        const { value } = e.target;
        setName(sanitizeName(value));
    };
    return (<Container
        fluid>
        <div
            className={'page--section mt-4'}>
            <h2>Register Account</h2>
            {isLoggedIn === false && (<>
                <Form
                    className={'col-4 mb-2'}
                    onSubmit={handleSubmit}>
                    <Row>
                        <Form.Group
                            className={'col-6 mb-2'}
                            controlId={'accountRegistrationForm.FirstName'}>
                            <Form.Label>First name</Form.Label>
                            <Form.Control
                                type={'text'}
                                name={'first-name'}
                                placeholder={'Your first name*'}
                                value={firstName}
                                onChange={(e) => updateName(e, setFirstName)}
                                isValid={sanitizeName(firstName)}
                                isInvalid={firstName !== '' && !sanitizeName(firstName)} />
                        </Form.Group>
                        <Form.Group
                            className={'col-6 mb-2'}
                            controlId={'accountRegistrationForm.LastName'}>
                            <Form.Label>Last name</Form.Label>
                            <Form.Control
                                type={'text'}
                                name={'last-name'}
                                placeholder={'Your last name'}
                                value={lastName}
                                onChange={(e) => updateName(e, setLastName)}
                                isValid={sanitizeName(lastName)}
                                isInvalid={lastName !== '' && !sanitizeName(lastName)} />
                        </Form.Group>
                    </Row>
                    <Form.Group
                        className={'mb-2'}
                        controlId={'accountRegistrationForm.Email'}>
                        <Form.Label>Your email</Form.Label>
                        <Form.Control
                            type={'text'}
                            name={'email'}
                            placeholder={'Enter your email address*'}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isValid={isEmail(email)}
                            isInvalid={email !== '' && !isEmail(email)} />
                    </Form.Group>
                    <Form.Group
                        className={'mb-2'}
                        controlId={'accountRegistrationForm.Phone'}>
                        <Form.Label>Your phone</Form.Label>
                        <Form.Control
                            type={'text'}
                            name={'phone'}
                            placeholder={'Enter your phone number'}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            isValid={isMobilePhone(phone)}
                            isInvalid={phone !== '' && !isMobilePhone(phone)} />
                    </Form.Group>
                    <Form.Group
                        className={'mb-2'}
                        controlId={'accountRegistrationForm.Password'}>
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                            autoComplete={'new-password'}
                            type={'password'}
                            name={'password'}
                            placeholder={'Enter a strong password*'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            isValid={isStrongPassword(password)}
                            isInvalid={password !== '' && !isStrongPassword(password)} />
                        <p
                            className={'mt-3'}>
                            Strong password criteria:
                            <ul>
                                <li>A length of 8 characters or more</li>
                                <li>Contains at least 1 occurrence of the following:</li>
                                <ul>
                                    <li>An uppercase letter (A-Z)</li>
                                    <li>A lowercase letter (a-z)</li>
                                    <li>A number (0-9)</li>
                                    <li>A special character (e.g. !@#$%)</li>
                                </ul>
                            </ul>
                        </p>
                    </Form.Group>
                    <Form.Group
                        className={'d-flex mb-2'}
                        controlId={'accountRegistrationForm.AcceptsMarketing'}>
                        <Form.Check
                            className={'me-2'}
                            name={'accepts-marketing'}
                            value={acceptsMarketing}
                            onChange={(e) => setAcceptsMarketing(!acceptsMarketing)} />
                        <Form.Label>Would you like to receive marketing emails and product updates?</Form.Label>
                    </Form.Group>
                    <Form.Group
                        controlId={'accountRegistrationForm.Submit'}>
                        <Button
                            type={'submit'}
                            onClick={handleSubmit}
                            disabled={!firstName || !email || !isEmail(email) || !isStrongPassword(password) || (isProcessing === true && startRegistration === true)}>Submit</Button>
                    </Form.Group>
                </Form>
            </>)}
            {isLoggedIn === true && (<p>You are already logged in.</p>)}
            {startRegistration === true && (<>
                {!isValidRequest && (<p>Invalid registration request.</p>)}
                {isProcessing === true && (<p>Processing request...</p>)}
                {(isProcessing === false && isProcessed === false) && (<p>Request failed.</p>)}
                {isProcessed === true && (
                    <p
                        style={{ color: 'green' }}>You are now registered. You may use your email and password to login to your new account. Thank you!</p>
                )}
                {(!isProcessing && errorMessage !== null) && (
                    <p
                        style={{ color: 'red' }}>Error: {errorMessage ?? 'Failed.'}</p>
                )}
            </>)}
        </div>
    </Container>);
};

AccountRegistrationPage.propTypes = {
    isMobile: bool,
    isLoggedIn: bool,
};

export default AccountRegistrationPage;